import React, { useEffect, useState } from 'react';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { paymentService } from '@/services/paymentService';
import { StudentPayment } from '@/types';
import { format } from 'date-fns';
import { Loader2, Download, Eye, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Extend jsPDF with autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

const StudentPayments = () => {
  const { user } = useAuth();
  const [payments, setPayments] = useState<StudentPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState<StudentPayment | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  useEffect(() => {
    const loadPayments = async () => {
      if (!user) {
        console.log("No user found");
        return;
      }

      console.log("Current user:", {
        id: user.id,
        email: user.email
      });

      try {
        console.log("Fetching payments for user ID:", user.id);
        const studentPayments = await paymentService.getStudentPayments(user.id);
        console.log("Raw payments data:", studentPayments);
        
        if (studentPayments.length === 0) {
          console.log("No payments found for user ID:", user.id);
        } else {
          console.log("Found", studentPayments.length, "payments");
          studentPayments.forEach((payment, index) => {
            console.log(`Payment ${index + 1}:`, {
              id: payment.id,
              studentId: payment.studentId,
              amount: payment.amount,
              status: payment.status,
              courseName: payment.courseName
            });
          });
        }
        
        setPayments(studentPayments);
      } catch (error) {
        console.error('Error loading payment history:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPayments();
  }, [user]);

  const addHeader = (doc: jsPDF, title: string) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Add a colored header background
    doc.setFillColor(41, 128, 185); // Blue color
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    // Add white text
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.text(title, pageWidth / 2, 25, { align: 'center' });
    
    // Reset text color
    doc.setTextColor(0, 0, 0);
  };

  const addFooter = (doc: jsPDF) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Add footer text
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('WAY2LEARN DRIVING SCHOOL', pageWidth / 2, pageHeight - 20, { align: 'center' });
    doc.text('167 - 169 Great Portland Street, London, W1W 5PF', pageWidth / 2, pageHeight - 15, { align: 'center' });
    doc.text('This is an official document', pageWidth / 2, pageHeight - 10, { align: 'center' });
  };

  const handleDownloadReport = async () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Add header
      addHeader(doc, 'Payment History Report');
      
      // Add student information in a styled box
      doc.setFillColor(240, 240, 240);
      doc.rect(20, 50, pageWidth - 40, 40, 'F');
      
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Student Name: ${user?.firstName} ${user?.lastName}`, 30, 65);
      doc.text(`Email: ${user?.email}`, 30, 75);
      doc.text(`Report Date: ${format(new Date(), 'PPp')}`, 30, 85);
      
      // Add payments table
      const tableColumn = ['Course', 'Amount', 'Status', 'Method', 'Date'];
      const tableRows = payments.map(payment => [
        payment.courseName,
        `£${payment.amount.toFixed(2)}`,
        payment.status.toUpperCase(),
        payment.paymentMethod,
        format(payment.createdAt, 'PPp')
      ]);
      
      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 100,
        theme: 'grid',
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontSize: 12,
          fontStyle: 'bold'
        },
        styles: {
          fontSize: 10,
          cellPadding: 5
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        }
      });
      
      // Add summary
      const finalY = (doc as any).lastAutoTable.finalY + 20;
      doc.setFontSize(14);
      doc.text('Summary', 20, finalY);
      
      const totalAmount = payments.reduce((sum, payment) => sum + payment.amount, 0);
      const paidPayments = payments.filter(p => p.status === 'paid').length;
      
      doc.setFontSize(12);
      doc.text(`Total Payments: ${payments.length}`, 30, finalY + 15);
      doc.text(`Paid Payments: ${paidPayments}`, 30, finalY + 25);
      doc.text(`Total Amount: £${totalAmount.toFixed(2)}`, 30, finalY + 35);
      
      // Add footer
      addFooter(doc);
      
      // Save the PDF
      doc.save(`payment-report-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    } catch (error) {
      console.error('Error downloading report:', error);
    }
  };

  const handleDownloadInvoice = (payment: StudentPayment) => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Add header
      addHeader(doc, 'Payment Invoice');
      
      // Add invoice details in a styled box
      doc.setFillColor(240, 240, 240);
      doc.rect(20, 50, pageWidth - 40, 30, 'F');
      
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Invoice Number: ${payment.id}`, 30, 65);
      doc.text(`Date: ${format(payment.createdAt, 'PPp')}`, 30, 75);
      
      // Add student information
      doc.setFontSize(14);
      doc.setTextColor(41, 128, 185);
      doc.text('Student Information', 20, 100);
      
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Name: ${payment.studentName}`, 30, 115);
      doc.text(`Email: ${payment.studentEmail}`, 30, 125);
      if (payment.studentPhone) {
        doc.text(`Phone: ${payment.studentPhone}`, 30, 135);
      }
      
      // Add course and payment details in a table
      const tableColumn = ['Description', 'Details'];
      const tableRows = [
        ['Course', payment.courseName],
        ['Amount', `£${payment.amount.toFixed(2)}`],
        ['Status', payment.status.toUpperCase()],
        ['Payment Method', payment.paymentMethod],
        ['Created', format(payment.createdAt, 'PPp')]
      ];
      
      if (payment.paymentDate) {
        tableRows.push(['Paid', format(payment.paymentDate, 'PPp')]);
      }
      
      if (payment.adminName) {
        tableRows.push(['Marked as paid by', payment.adminName]);
      }
      
      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 150,
        theme: 'grid',
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontSize: 12,
          fontStyle: 'bold'
        },
        styles: {
          fontSize: 10,
          cellPadding: 5
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        }
      });
      
      // Add footer
      addFooter(doc);
      
      // Save the PDF
      doc.save(`invoice-${payment.id}.pdf`);
    } catch (error) {
      console.error('Error downloading invoice:', error);
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="student">
        <div className="container mx-auto p-6">
          <h1 className="text-2xl font-bold mb-6">My Payments</h1>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-center space-x-2">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span>Loading payment history...</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  if (payments.length === 0) {
    return (
      <DashboardLayout role="student">
        <div className="container mx-auto p-6">
          <h1 className="text-2xl font-bold mb-6">My Payments</h1>
          <Card>
            <CardContent className="p-6">
              <div className="text-center text-muted-foreground">
                No payment history found for {user?.email}
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="student">
      <div className="container mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">My Payments</h1>
          <Button onClick={handleDownloadReport} className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Download Full Report
          </Button>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Payment History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {payments.map((payment) => (
                <div
                  key={payment.id}
                  className="p-4 border rounded-lg space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{payment.courseName}</p>
                      <p className="text-sm text-muted-foreground">
                        Amount: £{payment.amount.toFixed(2)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={payment.status === 'paid' ? 'default' : 'secondary'}
                      >
                        {payment.status.toUpperCase()}
                      </Badge>
                      <Dialog open={isViewDialogOpen && selectedPayment?.id === payment.id} onOpenChange={(open) => {
                        setIsViewDialogOpen(open);
                        if (!open) setSelectedPayment(null);
                      }}>
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedPayment(payment);
                              setIsViewDialogOpen(true);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Payment Details</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <h3 className="font-medium">Course Information</h3>
                              <p className="text-sm text-muted-foreground">{payment.courseName}</p>
                            </div>
                            <div>
                              <h3 className="font-medium">Payment Information</h3>
                              <p className="text-sm text-muted-foreground">Amount: £{payment.amount.toFixed(2)}</p>
                              <p className="text-sm text-muted-foreground">Status: {payment.status.toUpperCase()}</p>
                              <p className="text-sm text-muted-foreground">Method: {payment.paymentMethod}</p>
                              <p className="text-sm text-muted-foreground">Created: {format(payment.createdAt, 'PPp')}</p>
                              {payment.paymentDate && (
                                <p className="text-sm text-muted-foreground">Paid: {format(payment.paymentDate, 'PPp')}</p>
                              )}
                            </div>
                            <div>
                              <h3 className="font-medium">Student Information</h3>
                              <p className="text-sm text-muted-foreground">Name: {payment.studentName}</p>
                              <p className="text-sm text-muted-foreground">Email: {payment.studentEmail}</p>
                              {payment.studentPhone && (
                                <p className="text-sm text-muted-foreground">Phone: {payment.studentPhone}</p>
                              )}
                            </div>
                            {payment.adminName && (
                              <div>
                                <h3 className="font-medium">Admin Information</h3>
                                <p className="text-sm text-muted-foreground">Marked as paid by: {payment.adminName}</p>
                              </div>
                            )}
                          </div>
                        </DialogContent>
                      </Dialog>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadInvoice(payment)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>Payment Method: {payment.paymentMethod}</p>
                    <p>Created: {format(payment.createdAt, 'PPp')}</p>
                    {payment.paymentDate && (
                      <p>Paid: {format(payment.paymentDate, 'PPp')}</p>
                    )}
                    {payment.adminName && (
                      <p>Marked as paid by: {payment.adminName}</p>
                    )}
                    <p>Email: {payment.studentEmail}</p>
                    {payment.studentPhone && (
                      <p>Phone: {payment.studentPhone}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default StudentPayments; 